import React, { useEffect, useState } from "react";
import { Stethoscope, ChevronRight, Calendar, Clock, User, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api from "../../../api";
import {
  apiRequest,
  apiSuccess,
  apiFetchError,
} from "../../../redux/api/apiActions";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import typeTranslator from "../../../utils/useTypeTranslator";
import Txt from "../../../components/Text";
import formatDate from "../../../utils/formatDate";
import formatTime from "../../../utils/formatTime";
import StatusAlert from "react-status-alert";
import { isAdminRole } from "../../../utils/adminChecker";
import { FaUserDoctor } from "react-icons/fa6";
import { checkAccess } from "../../../utils/usePageAccess";

/**
 * Text Component Wrapper for Waiting Room Page
 * Props:
 * - children: Text content
 * - className: Additional CSS classes
 * @returns Text JSX
 */

const Text = ({ children, className, page = "VISITS" }) => {
  return (
    <Txt page={page} className={className}>
      {children}
    </Txt>
  );
};

// Placeholder component for RecentVisits
const RecentVisitsPlaceholder = ({ darkMode = false }) => {
  return (
    <div
      className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                    bg-transparent shadow-none border-0
                    ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
    >
      {/* Title Placeholder with shimmer */}
      <div className={arClass("relative overflow-hidden mb-3.5")}>
        <div
          className={arClass(`h-5 w-32 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`)}
        />
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>

      {/* Visit Items Placeholder */}
      <div className={arClass("flex flex-col")}>
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={arClass(`relative overflow-hidden flex items-center justify-between py-2.5 ${i < 2 ? `border-b ${darkMode ? "border-gray-700" : "border-slate-100"}` : ""}`)}
          >
            <div className={arClass("flex items-center gap-2.5 flex-1")}>
              <div
                className={arClass(`w-8 h-8 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-200"} flex-shrink-0`)}
              />
              <div className={arClass("flex-1")}>
                <div
                  className={arClass(`h-4 w-28 rounded mb-1 ${darkMode ? "bg-gray-700" : "bg-gray-200"}`)}
                />
                <div
                  className={arClass(`h-3 w-32 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`)}
                />
              </div>
            </div>
            <div className={arClass("flex items-center gap-1.5 flex-shrink-0 ml-4")}>
              <div
                className={arClass(`h-5 w-20 rounded-lg ${darkMode ? "bg-gray-700" : "bg-gray-200"}`)}
              />
              <div
                className={arClass(`w-3.5 h-3.5 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`)}
              />
            </div>
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        ))}
      </div>

      <div className={arClass("relative overflow-hidden mt-3.5")}>
        <div
          className={arClass(`h-4 w-28 rounded ${darkMode ? "bg-gray-700" : "bg-gray-200"}`)}
        />
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </div>
    </div>
  );
};

const getStuts = (status, darkMode, defaultLanguage) => {
  const colors = {
    waiting: darkMode
      ? "bg-blue-800 text-blue-100"
      : "bg-blue-100 text-blue-800",
    with_nurse: darkMode
      ? "bg-violet-800 text-violet-100"
      : "bg-violet-100 text-violet-800",
    with_doctor: darkMode
      ? "bg-emerald-800 text-emerald-100"
      : "bg-emerald-100 text-emerald-800",
    done: darkMode ? "bg-teal-800 text-teal-100" : "bg-teal-100 text-teal-800",
    cancelled: darkMode
      ? "bg-rose-800 text-rose-100"
      : "bg-rose-100 text-rose-800",
    absent: darkMode
      ? "bg-amber-800 text-amber-100"
      : "bg-amber-100 text-amber-800",
    payment: darkMode
      ? "bg-yellow-800 text-yellow-100"
      : "bg-yellow-100 text-yellow-800",
  };
  const borderStyles = {
    waiting: darkMode
      ? "border-l-[6px] border-l-blue-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-blue-500 max-sm:border-l-[3px]",
    with_nurse: darkMode
      ? "border-l-[6px] border-l-violet-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-violet-500 max-sm:border-l-[3px]",
    with_doctor: darkMode
      ? "border-l-[6px] border-l-emerald-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-emerald-500 max-sm:border-l-[3px]",
    done: darkMode
      ? "border-l-[6px] border-l-teal-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-teal-500 max-sm:border-l-[3px]",
    cancelled: darkMode
      ? "border-l-[6px] border-l-rose-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-rose-500 max-sm:border-l-[3px]",
    absent: darkMode
      ? "border-l-[6px] border-l-amber-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-amber-500 max-sm:border-l-[3px]",
    payment: darkMode
      ? "border-l-[6px] border-l-yellow-300 max-sm:border-l-[3px]"
      : "border-l-[6px] border-l-yellow-500 max-sm:border-l-[3px]",
  };

  const borderStylesRtl = {
    waiting: darkMode
      ? "border-r-[6px] border-r-blue-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-blue-500 max-sm:border-r-[3px]",
    with_nurse: darkMode
      ? "border-r-[6px] border-r-violet-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-violet-500 max-sm:border-r-[3px]",
    with_doctor: darkMode
      ? "border-r-[6px] border-r-emerald-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-emerald-500 max-sm:border-r-[3px]",
    done: darkMode
      ? "border-r-[6px] border-r-teal-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-teal-500 max-sm:border-r-[3px]",
    cancelled: darkMode
      ? "border-r-[6px] border-r-rose-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-rose-500 max-sm:border-r-[3px]",
    absent: darkMode
      ? "border-r-[6px] border-r-amber-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-amber-500 max-sm:border-r-[3px]",
    payment: darkMode
      ? "border-r-[6px] border-r-yellow-300 max-sm:border-r-[3px]"
      : "border-r-[6px] border-r-yellow-500 max-sm:border-r-[3px]",
  };

  if (defaultLanguage === "ar") {
    return {
      status,
      color:
        colors[status] ||
        (darkMode
          ? "bg-slate-800 text-slate-100"
          : "bg-slate-100 text-slate-800"),
      border:
        borderStylesRtl[status] ||
        (darkMode
          ? "border-r-[6px] border-r-slate-700 max-sm:border-r-[3px]"
          : "border-r-[6px] border-r-slate-300 max-sm:border-r-[3px]"),
    };
  } else {
    return {
      status,
      color:
        colors[status] ||
        (darkMode
          ? "bg-slate-800 text-slate-100"
          : "bg-slate-100 text-slate-800"),
      border:
        borderStyles[status] ||
        (darkMode
          ? "border-l-[6px] border-l-slate-700 max-sm:border-l-[3px]"
          : "border-l-[6px] border-l-slate-300 max-sm:border-l-[3px]"),
    };
  }
};

/**
 * Badge class mappings
 * @returns Badge class object
 */
const statusBadgeClass = (darkMode, status) => {
  const defaultClasses = {
    waiting: darkMode
      ? "bg-blue-800 !text-blue-100 border border-blue-700"
      : "bg-blue-100 !text-blue-800 border border-blue-300",
    nurse: darkMode
      ? "bg-violet-800 !text-violet-100 border border-violet-700"
      : "bg-violet-100 !text-violet-800 border border-violet-300",
    doctor: darkMode
      ? "bg-emerald-800 !text-emerald-100 border border-emerald-700"
      : "bg-emerald-100 !text-emerald-800 border border-emerald-300",
    finished: darkMode
      ? "bg-teal-800 !text-teal-100 border border-teal-700"
      : "bg-teal-100 !text-teal-800 border border-teal-300",
    cancelled: darkMode
      ? "bg-rose-800 !text-rose-100 border border-rose-700"
      : "bg-rose-100 !text-rose-800 border border-rose-300",
    absent: darkMode
      ? "bg-amber-800 !text-amber-100 border border-amber-700"
      : "bg-amber-100 !text-amber-800 border border-amber-300",
    payment: darkMode
      ? "bg-yellow-800 !text-yellow-100 border border-yellow-700"
      : "bg-yellow-100 !text-yellow-800 border border-yellow-300",
  };
  return (
    defaultClasses[status] ||
    (darkMode
      ? "bg-slate-800 !text-slate-100 border border-slate-700"
      : "bg-slate-100 !text-slate-800 border border-slate-300")
  );
};

export default function RecentVisits() {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const { requests } = useSelector((state) => state.api);
  const { id } = useParams();
  const patientKey = `getPatient_${id}`;
  const visitsKey = `getVisitsByPatient_${id}`;
  const patientLoading = requests[patientKey]?.loading;
  const visitsLoading = requests[visitsKey]?.loading;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [visits, setVisits] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        dispatch(apiRequest("GET", visitsKey));
        const response = await api.get(
          `/visits/patient/${id}/?includeCreator=true&pageSize=5`,
        );

        setVisits(response.data.results || response.data);
        dispatch(apiSuccess("GET", visitsKey, "Fetched visits successfully"));
      } catch (error) {
        console.error("Error fetching visits:", error);
        setError(error.message || "Failed to fetch visits");
        dispatch(apiFetchError("GET", visitsKey, error.message));
      }
    };

    if (id) {
      fetchVisits();
    }
  }, [id, dispatch, visitsKey]);

  const handleViewAll = () => {
    navigate("/visits");
  };

  const handleVisitClick = (visitId) => {
    navigate(`/visits/${visitId}`);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0 },
  };

  // Helper function for dynamic classes
  const getStatusColors = (status) => {
    if (status === "active") {
      return {
        bg: darkMode ? "bg-green-900/30" : "bg-green-100",
        text: darkMode ? "text-green-400" : "text-green-600",
        badgeBg: darkMode ? "bg-green-900/30" : "bg-green-100",
        badgeText: darkMode ? "text-green-300" : "text-green-700",
      };
    } else if (status === "completed") {
      return {
        bg: darkMode ? "bg-blue-900/30" : "bg-blue-100",
        text: darkMode ? "text-blue-400" : "text-blue-600",
        badgeBg: darkMode ? "bg-blue-900/30" : "bg-blue-100",
        badgeText: darkMode ? "text-blue-300" : "text-blue-700",
      };
    } else {
      return {
        bg: darkMode ? "bg-gray-700" : "bg-gray-100",
        text: darkMode ? "text-gray-400" : "text-gray-500",
        badgeBg: darkMode ? "bg-gray-700" : "bg-gray-100",
        badgeText: darkMode ? "text-gray-400" : "text-gray-600",
      };
    }
  };

  const statusKeyMap = {
    waiting: "Waiting in the waiting room",
    nurse: "Patient triage with nurse",
    doctor: "Visit with doctor",
    finished: "Visit completed",
    cancelled: "Visit cancelled",
    absent: "Patient absent",
    payment: "Payment required",
  };
  // Show placeholder while loading
  if (patientLoading || visitsLoading) {
    return <RecentVisitsPlaceholder darkMode={darkMode} />;
  }

  // Show error state
  if (error) {
    return (
      <div
        className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                      bg-transparent shadow-none border-0
                      ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
      >
        <h3
          className={arClass(`mb-3.5 text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`)}
        >
          <Text>Recent Visits</Text>
        </h3>
        <div className={arClass("flex flex-col items-center justify-center py-8")}>
          <div className={arClass("text-red-500 mb-3")}>
            <Stethoscope
              className={arClass(`w-12 h-12 ${darkMode ? "text-red-400" : "text-red-500"}`)}
            />
          </div>
          <p
            className={arClass(`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`)}
          >
            Failed to load visits. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium ${
              darkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-blue-500 hover:bg-blue-600 text-white"
            }`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no visits
  if (!patientLoading && !visitsLoading && visits.length === 0) {
    return (
      <div
        className={arClass(`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                      bg-transparent shadow-none border-0
                      ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`)}
      >
        <h3
          className={arClass(`mb-3.5 text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`)}
        >
          <Text>Recent Visits</Text>
        </h3>
        <div className={arClass("flex flex-col items-center justify-center py-8")}>
          <Stethoscope
            className={arClass(`w-12 h-12 ${darkMode ? "text-gray-600" : "text-gray-300"} mb-3`)}
          />
          <p
            className={arClass(`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`)}
          >
            <Text>No visits found for this patient.</Text>
          </p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={`p-5 sm:rounded-2xl sm:shadow-[0_1px_4px_rgba(0,0,0,0.06)] sm:border sm:border-slate-100 overflow-hidden 
                    bg-transparent shadow-none border-0
                    ${darkMode ? "sm:bg-gray-800 sm:border-gray-700" : "sm:bg-white sm:border-slate-100"}`}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      <h3
        className={arClass(`mb-3.5 text-[13px] font-bold ${darkMode ? "text-gray-100" : "text-gray-900"}`)}
      >
        <Text>Recent Visits</Text> ({visits.length})
      </h3>

      <motion.div className={arClass("flex flex-col")}>
        {visits.map((v, i) => {
          const statusColors = getStatusColors(v.status);
          const isLast = i < Math.min(visits.length, 5) - 1;

          return (
            <motion.div
              key={v.id || i}
              className={arClass(`flex-col items-start flex  py-2.5 cursor-pointer transition-colors  ${
                isLast
                  ? `border-b ${darkMode ? "border-gray-700" : "border-slate-50"}`
                  : ""
              } ${darkMode ? "hover:bg-gray-700/50" : "hover:bg-slate-50"}`)}
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              onClick={() => handleVisitClick(v.id)}
            >
              <div className={arClass("flex items-center gap-2.5 flex-1 min-w-0 ")}>
                <div
                  className={arClass(`flex shrink-0 items-center justify-center w-8 h-8 rounded-full ${statusColors.bg}`)}
                >
                  <Stethoscope className={arClass(`w-3.5 h-3.5 ${statusColors.text}`)} />
                </div>
                <div className={arClass("flex-1 min-w-0")}>
                  <div className={arClass("flex items-center gap-2 flex-wrap")}>
                    <p
                      className={arClass(`text-[13px] font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`)}
                    >
                      {typeTranslator(v.serviceType.nameEn).translatedType ||
                        "General Visit"}
                    </p>
                    <span
                      className={arClass(`text-[10px] px-2 py-0.5 rounded-full ${statusColors.badgeBg} ${statusColors.badgeText}`)}
                    >
                      <Text>{v.status}</Text>
                    </span>
                  </div>
                  <div
                    className={arClass(`flex items-center gap-3 text-[11px] ${darkMode ? "text-gray-500" : "text-gray-400"}`)}
                  >
                    <span className={arClass("flex items-center gap-1")}>
                      <Calendar className={arClass("w-3 h-3")} />
                      {v.createdAt
                        ? formatDate(v.createdAt, true)
                        : "Unknown date"}
                    </span>
                  </div>
                </div>
              </div>

              <div className={"flex items-center gap-1.5 flex-shrink-0 ml-2 justify-end w-full mt-2"}>
                <span
                  className={arClass(
                    `px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide max-sm:text-xsm flex justify-center items-center ${
                      statusBadgeClass(darkMode, v.currentStage) ||
                      "bg-slate-100 text-slate-500 border border-slate-200"
                    }`,
                  )}
                >
                  <Text>{statusKeyMap[v.currentStage]}</Text>
                </span>

                {
                  defaultLanguage === "ar" ? (
                    <ChevronLeft
                      className={arClass(`w-3 h-3 ${darkMode ? "text-gray-400" : "text-gray-500"}`)}                    />
                  ) : (
                    <ChevronRight
                  className={arClass(`w-3.5 h-3.5 ${darkMode ? "text-gray-600" : "text-gray-300"}`)}
                />
                    )
                }
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {visits.length > 5 && (
        <button
          className={arClass(`mt-3.5 flex items-center gap-1 text-[12px] font-medium transition-colors ${
            darkMode
              ? "text-blue-400 hover:text-blue-300"
              : "text-blue-500 hover:text-blue-600"
          }`)}
          onClick={handleViewAll}
        >
          View all {visits.length} visits <ChevronRight className="w-3 h-3" />
        </button>
      )}

      {visits.length <= 5 && visits.length > 0 && (
        <button
          className={arClass(`mt-3.5 flex items-center gap-1 text-[12px] font-medium transition-colors ${
            darkMode
              ? "text-blue-400 hover:text-blue-300"
              : "text-blue-500 hover:text-blue-600"
          }`)}
          onClick={handleViewAll}
        >
          <Text>View all visits</Text> {
            defaultLanguage === "ar" ? (
              <ChevronLeft
                className={arClass(`w-3 h-3 ${darkMode ? "text-gray-400" : "text-gray-500"}`)}                    />
            ) : (
              <ChevronRight
            className={arClass(`w-3.5 h-3.5 ${darkMode ? "text-gray-600" : "text-gray-300"}`)}
          />
              )
          }
        </button>
      )}
    </motion.div>
  );
}
